/** @jsxImportSource @emotion/react */

'use client';

import { Box } from '@mui/material';
import type { ReactNode } from 'react';

import { Button } from '@campaign-manager/react/components';

import * as styles from './styles';

export interface WizardHeaderProps {
  /** Logo element to display on the left */
  logo: ReactNode;

  /** Label for the "previous step" button */
  prevStepButtonLabel?: string;
  /** Label for the "next step" button */
  nextStepButtonLabel?: string;
  /** Optional label for an alternative primary button (e.g., "Save & Activate") */
  altPrimaryButtonLabel?: string;

  /** Callback when "next step" button is clicked */
  onNextStepButtonClick?: () => void;
  /** Callback when "previous step" button is clicked */
  onPrevStepButtonClick?: () => void;
  /** Callback when alternative primary button is clicked */
  onAltPrimaryButtonClick?: () => void;

  /** Whether the "previous step" button is disabled */
  prevStepButtonDisabled?: boolean;
  /** Whether the "next step" button is disabled */
  nextStepButtonDisabled?: boolean;
  /** Whether the alternative primary button is disabled */
  altPrimaryButtonDisabled?: boolean;
}

/**
 * WizardHeader - Header for wizard layouts with logo and action buttons
 *
 * Follows the Linear/Stripe pattern of putting navigation actions in the header
 * rather than a fixed bottom bar. This keeps actions close to content and
 * creates a cleaner, more modern layout.
 *
 * Layout: [Logo] ... [Back] [Next/Continue]
 */
export default function WizardHeader({
  logo,
  nextStepButtonLabel,
  prevStepButtonLabel,
  altPrimaryButtonLabel,
  onNextStepButtonClick,
  onPrevStepButtonClick,
  onAltPrimaryButtonClick,
  prevStepButtonDisabled,
  nextStepButtonDisabled,
  altPrimaryButtonDisabled,
}: WizardHeaderProps) {
  return (
    <Box css={styles.wizardHeaderContent}>
      {/* Left side - logo */}
      <Box css={styles.wizardHeaderLogo}>{logo}</Box>

      {/* Right side - action buttons */}
      <Box css={styles.wizardHeaderActions}>
        {prevStepButtonLabel && onPrevStepButtonClick && (
          <Button
            type="button"
            onClick={onPrevStepButtonClick}
            disabled={prevStepButtonDisabled}
            size="medium"
            color="secondary"
            variant="text"
          >
            {prevStepButtonLabel}
          </Button>
        )}
        {nextStepButtonLabel && (
          <Button
            type="button"
            onClick={onNextStepButtonClick}
            disabled={nextStepButtonDisabled}
            size="medium"
            variant="contained"
            color={altPrimaryButtonLabel ? 'inherit' : 'primary'}
          >
            {nextStepButtonLabel}
          </Button>
        )}
        {altPrimaryButtonLabel && (
          <Button
            type="button"
            onClick={onAltPrimaryButtonClick}
            disabled={altPrimaryButtonDisabled}
            size="medium"
            variant="contained"
            color="primary"
          >
            {altPrimaryButtonLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
}
